/**
 * KYC DOCUMENT VIEWER COMPONENT
 *
 * Advanced document viewer for KYC submissions with:
 * - Image preview with zoom and pan
 * - Document-specific approval/rejection
 * - Side-by-side comparison for ID front/back
 * - Download functionality
 * - Audit trail logging
 */

import React, { useState, useEffect } from 'react';
import { getSessionHeaders } from '../../lib/serverSession';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

interface KYCDocument {
  id: string;
  type: 'id_document' | 'selfie' | 'proof_of_address' | 'company_registration';
  fileName: string;
  url: string;
  uploadedAt: string;
  verificationStatus?: 'pending' | 'approved' | 'rejected' | 'requires_resubmission' | 'expired';
  reviewedBy?: string;
  reviewedAt?: string;
  rejectionReason?: string;
  adminNotes?: string;
}

interface KYCDocumentViewerProps {
  kycData: any;
  onDocumentStatusChange?: () => void;
  currentUser: any;
  documentType?: 'id_document' | 'selfie' | 'proof_of_address' | 'company_registration';
}

const KYCDocumentViewer: React.FC<KYCDocumentViewerProps> = ({
  kycData,
  documentType
}) => {
  const [documents, setDocuments] = useState<KYCDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDocument, setSelectedDocument] = useState<KYCDocument | null>(null);
  const [showImageModal, setShowImageModal] = useState(false);

  // Load documents from storage
  useEffect(() => {
    loadDocuments();
  }, [kycData]);

  const loadDocuments = async () => {
    if (!kycData?.user_id) return;

    setLoading(true);
    try {
      const currentUserId = kycData.user_id;

      console.log('🔍 [KYCDocumentViewer] Fetching documents for user ID:', currentUserId);
      console.log('🔍 [KYCDocumentViewer] User full name:', kycData.full_legal_name);

      // Fetch documents via API endpoint
      const response = await fetch(`${API_BASE_URL}/api/admin/kyc`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          operation: 'list_user_documents',
          data: { userId: currentUserId }
        })
      });
      const result = await response.json();

      if (!response.ok) {
        console.error('❌ [KYCDocumentViewer] Error fetching documents:', result.error);
        setDocuments([]);
        return;
      }

      const documentList = (result.data || []) as KYCDocument[];
      console.log('📋 [KYCDocumentViewer] Final document list for user:', documentList);
      setDocuments(documentList);

    } catch (error) {
      console.error('❌ Error loading documents:', error);
    } finally {
      setLoading(false);
    }
  };

  // Document type display names
  const getDocumentTypeName = (type: string) => {
    const typeNames: Record<string, string> = {
      'id_document': 'ID Document',
      'proof_of_address': 'Proof of Address',
      'selfie': 'Selfie Verification',
      'company_registration': 'Company Registration Document'
    };
    return typeNames[type] || type;
  };

  // Get document type icon
  const getDocumentIcon = (type: string) => {
    switch (type) {
      case 'id_document': return '🆔';
      case 'proof_of_address': return '🏠';
      case 'selfie': return '🤳';
      case 'company_registration': return '🏢';
      default: return '📄';
    }
  };

  // Handle document selection for viewing
  const handleDocumentSelect = (document: KYCDocument) => {
    setSelectedDocument(document);
    setShowImageModal(true);
  };

  // Handle document download
  const handleDownloadDocument = (document: KYCDocument) => {
    try {
      const link = window.document.createElement('a');
      link.href = document.url;
      link.download = document.fileName;
      link.target = '_blank';
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      console.log(`✅ Downloaded document: ${document.fileName}`);
    } catch (error) {
      console.error('❌ Error downloading document:', error);
      // Fallback: open in new tab
      window.open(document.url, '_blank');
    }
  };

  // Handle opening document in new window
  const handleOpenDocument = (document: KYCDocument) => {
    try {
      window.open(document.url, '_blank', 'noopener,noreferrer');
      console.log(`✅ Opened document in new window: ${document.fileName}`);
    } catch (error) {
      console.error('❌ Error opening document:', error);
      alert('Unable to open document in new window. Please check your popup blocker settings.');
    }
  };

  // Decrypt ID number for display
  const decryptIdNumber = (encryptedId: string): string => {
    try {
      if (!encryptedId) return 'N/A';
      return atob(encryptedId); // Base64 decode
    } catch (error) {
      return 'Error decrypting';
    }
  };

  if (loading) {
    return (
      <div className="glass-card p-8 text-center">
        <div className="text-4xl mb-4">⏳</div>
        <p className="text-white">Loading documents...</p>
      </div>
    );
  }

  // Filter documents by type if specified
  const filteredDocuments = documentType
    ? documents.filter(doc => doc.type === documentType)
    : documents;

  console.log(`🔍 KYCDocumentViewer for type "${documentType}":`, {
    totalDocuments: documents.length,
    filteredDocuments: filteredDocuments.length,
    documentType,
    filteredDocs: filteredDocuments.map(doc => ({
      fileName: doc.fileName,
      type: doc.type,
      uploadedAt: doc.uploadedAt
    }))
  });

  if (filteredDocuments.length === 0) {
    const documentTypeName = documentType ? getDocumentTypeName(documentType) : 'documents';
    return (
      <div className="glass-card p-8 text-center">
        <div className="text-6xl mb-4">📄</div>
        <p className="text-white text-lg">No {documentTypeName.toLowerCase()} uploaded</p>
        <p className="text-gray-500 text-sm mt-2">
          This KYC submission doesn't have any {documentTypeName.toLowerCase()} yet.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Documents Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredDocuments.map((document) => (
          <div
            key={document.id}
            className="bg-gray-800/50 rounded-lg p-4 border border-gray-700 hover:border-yellow-500/50 transition-colors"
          >
            {/* Document Header */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <span className="text-2xl">{getDocumentIcon(document.type)}</span>
                <div>
                  <h4 className="font-medium text-white text-sm">
                    {getDocumentTypeName(document.type)}
                  </h4>
                  <p className="text-sm text-white mt-1">
                    {document.fileName}
                  </p>
                </div>
              </div>
              {/* Verification Status Badge */}
              {document.verificationStatus && (
                <div className={`px-2 py-1 rounded-full text-sm font-medium ${
                  document.verificationStatus === 'pending' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' :
                  document.verificationStatus === 'approved' ? 'bg-green-500/20 text-green-400 border border-green-500/30' :
                  document.verificationStatus === 'rejected' ? 'bg-red-500/20 text-red-400 border border-red-500/30' :
                  'bg-gray-500/20 text-white border border-gray-500/30'
                }`}>
                  {document.verificationStatus === 'pending' ? '⏳ Pending' :
                   document.verificationStatus === 'approved' ? '✅ Approved' :
                   document.verificationStatus === 'rejected' ? '❌ Rejected' :
                   document.verificationStatus}
                </div>
              )}
            </div>

            {/* Document Preview */}
            <div
              className="aspect-video bg-gray-900 rounded-lg mb-3 flex items-center justify-center overflow-hidden cursor-pointer hover:bg-gray-800 transition-colors"
              onClick={() => handleDocumentSelect(document)}
            >
              <img
                src={document.url}
                alt={document.fileName}
                className="max-w-full max-h-full object-contain"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.nextElementSibling?.classList.remove('hidden');
                }}
              />
              <div className="text-white text-center hidden">
                <div className="text-2xl mb-2">📄</div>
                <div className="text-sm">Click to view</div>
              </div>
            </div>

            {/* Document Info */}
            <div className="text-sm text-white space-y-1 mb-3">
              <div>Uploaded: {new Date(document.uploadedAt).toLocaleDateString()}</div>
            </div>

            {/* Actions */}
            <div className="flex space-x-2">
              <button
                onClick={() => handleDocumentSelect(document)}
                className="flex-1 px-3 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm rounded transition-colors"
              >
                👁️ View
              </button>
              <button
                onClick={() => handleOpenDocument(document)}
                className="px-3 py-2 bg-green-600 hover:bg-green-500 text-white text-sm rounded transition-colors"
                title="Open in new window"
              >
                🔗
              </button>
              <button
                onClick={() => handleDownloadDocument(document)}
                className="px-3 py-2 bg-gray-600 hover:bg-gray-500 text-white text-sm rounded transition-colors"
                title="Download document"
              >
                ⬇️
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Enhanced Image Modal with Verification Data */}
      {showImageModal && selectedDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
          <div className="max-w-7xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="bg-gray-800 rounded-t-lg p-4 flex justify-between items-center border-b border-gray-700">
              <div className="flex items-center space-x-3">
                <span className="text-2xl">{getDocumentIcon(selectedDocument.type)}</span>
                <div>
                  <h3 className="text-lg font-semibold text-white">
                    {getDocumentTypeName(selectedDocument.type)} - Verification View
                  </h3>
                  <p className="text-sm text-white">{selectedDocument.fileName}</p>
                </div>
              </div>
              <button
                onClick={() => setShowImageModal(false)}
                className="text-gray-400 hover:text-white text-2xl"
              >
                ✕
              </button>
            </div>

            {/* Two-Column Layout: Document + Verification Data */}
            <div className="bg-gray-900 rounded-b-lg">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
                {/* Left Column: Document Image */}
                <div className="space-y-4">
                  <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                    <h4 className="text-white font-semibold mb-3 flex items-center">
                      <span className="mr-2">📄</span>
                      Document Image
                    </h4>
                    <div className="bg-black rounded-lg p-4 flex items-center justify-center min-h-[400px]">
                      <img
                        src={selectedDocument.url}
                        alt={selectedDocument.fileName}
                        className="max-w-full max-h-[600px] object-contain rounded"
                      />
                    </div>
                    <div className="mt-3 text-sm text-white">
                      <div>Uploaded: {new Date(selectedDocument.uploadedAt).toLocaleString()}</div>
                      <div className="mt-1">File: {selectedDocument.fileName}</div>
                    </div>
                  </div>
                </div>

                {/* Right Column: Verification Data */}
                <div className="space-y-4">
                  {/* ID Document Verification Data */}
                  {selectedDocument.type === 'id_document' && (
                    <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                      <h4 className="text-white font-semibold mb-4 flex items-center">
                        <span className="mr-2">🆔</span>
                        ID Document Verification Data
                      </h4>
                      <div className="space-y-3">
                        <div className="bg-gray-750 rounded p-3 border border-gray-600">
                          <label className="text-sm text-white block mb-1">Full Legal Name</label>
                          <div className="text-white font-medium">
                            {kycData?.full_legal_name || `${kycData?.first_name || ''} ${kycData?.last_name || ''}`.trim() || 'N/A'}
                          </div>
                        </div>

                        <div className="bg-gray-750 rounded p-3 border border-gray-600">
                          <label className="text-sm text-white block mb-1">
                            {kycData?.id_type === 'passport' ? 'Passport Number' : 'ID Number'}
                          </label>
                          <div className="text-white font-medium font-mono">
                            {kycData?.id_number_encrypted ? decryptIdNumber(kycData.id_number_encrypted) : 'N/A'}
                          </div>
                        </div>

                        <div className="bg-gray-750 rounded p-3 border border-gray-600">
                          <label className="text-sm text-white block mb-1">Date of Birth</label>
                          <div className="text-white font-medium">
                            {kycData?.date_of_birth ? new Date(kycData.date_of_birth).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            }) : 'N/A'}
                          </div>
                        </div>

                        <div className="bg-gray-750 rounded p-3 border border-gray-600">
                          <label className="text-sm text-white block mb-1">Nationality</label>
                          <div className="text-white font-medium">{kycData?.nationality || kycData?.country_name || 'N/A'}</div>
                        </div>

                        <div className="bg-gray-750 rounded p-3 border border-gray-600">
                          <label className="text-sm text-white block mb-1">ID Type</label>
                          <div className="text-white font-medium capitalize">
                            {kycData?.id_type?.replace(/_/g, ' ') || 'N/A'}
                          </div>
                        </div>
                      </div>

                      <div className="mt-4 p-3 bg-blue-900/20 border border-blue-700 rounded">
                        <p className="text-blue-400 text-sm">
                          <strong>✓ Verification Tip:</strong> Compare the name, ID number, date of birth, and nationality on the document image with the data shown here. All fields should match exactly.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Proof of Address Verification Data */}
                  {selectedDocument.type === 'proof_of_address' && (
                    <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                      <h4 className="text-white font-semibold mb-4 flex items-center">
                        <span className="mr-2">🏠</span>
                        Address Verification Data
                      </h4>
                      <div className="space-y-3">
                        <div className="bg-gray-750 rounded p-3 border border-gray-600">
                          <label className="text-sm text-white block mb-1">Full Legal Name</label>
                          <div className="text-white font-medium">
                            {kycData?.full_legal_name || `${kycData?.first_name || ''} ${kycData?.last_name || ''}`.trim() || 'N/A'}
                          </div>
                        </div>

                        <div className="bg-gray-750 rounded p-3 border border-gray-600">
                          <label className="text-sm text-white block mb-1">Residential Address</label>
                          <div className="text-white font-medium whitespace-pre-line">
                            {kycData?.street_address || kycData?.residential_address || 'N/A'}
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div className="bg-gray-750 rounded p-3 border border-gray-600">
                            <label className="text-sm text-white block mb-1">City</label>
                            <div className="text-white font-medium">{kycData?.city || 'N/A'}</div>
                          </div>

                          <div className="bg-gray-750 rounded p-3 border border-gray-600">
                            <label className="text-sm text-white block mb-1">Postal Code</label>
                            <div className="text-white font-medium">{kycData?.postal_code || 'N/A'}</div>
                          </div>
                        </div>

                        <div className="bg-gray-750 rounded p-3 border border-gray-600">
                          <label className="text-sm text-white block mb-1">Country of Residence</label>
                          <div className="text-white font-medium">
                            {kycData?.country_name || kycData?.country_of_residence || 'N/A'}
                          </div>
                        </div>
                      </div>

                      <div className="mt-4 p-3 bg-blue-900/20 border border-blue-700 rounded">
                        <p className="text-blue-400 text-sm">
                          <strong>✓ Verification Tip:</strong> Verify that the name and address on the document match the submitted data. The document should be recent (typically within 3 months) and show the full address.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Selfie Verification Data */}
                  {selectedDocument.type === 'selfie' && (
                    <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                      <h4 className="text-white font-semibold mb-4 flex items-center">
                        <span className="mr-2">🤳</span>
                        Selfie Verification Data
                      </h4>
                      <div className="space-y-3">
                        <div className="bg-gray-750 rounded p-3 border border-gray-600">
                          <label className="text-sm text-white block mb-1">Full Legal Name</label>
                          <div className="text-white font-medium">{kycData?.full_legal_name || 'N/A'}</div>
                        </div>

                        <div className="bg-gray-750 rounded p-3 border border-gray-600">
                          <label className="text-sm text-white block mb-1">Facial Recognition Status</label>
                          <div className="text-white font-medium">
                            {kycData?.facial_recognition_verified ? '✅ Verified' : '⏳ Pending Verification'}
                          </div>
                        </div>

                        {kycData?.facial_confidence_score && (
                          <div className="bg-gray-750 rounded p-3 border border-gray-600">
                            <label className="text-sm text-white block mb-1">Confidence Score</label>
                            <div className="text-white font-medium">
                              {(kycData.facial_confidence_score * 100).toFixed(1)}%
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="mt-4 p-3 bg-blue-900/20 border border-blue-700 rounded">
                        <p className="text-blue-400 text-sm">
                          <strong>✓ Verification Tip:</strong> Compare the selfie with the ID document photo. The person should be clearly visible, facing the camera, and match the ID photo.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Additional User Information */}
                  <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                    <h4 className="text-white font-semibold mb-4 flex items-center">
                      <span className="mr-2">👤</span>
                      Additional User Information
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between py-2 border-b border-gray-700">
                        <span className="text-white">User ID:</span>
                        <span className="text-white font-medium">#{kycData?.user_id}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-gray-700">
                        <span className="text-white">Email:</span>
                        <span className="text-white font-medium">{kycData?.email_address || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-gray-700">
                        <span className="text-white">Phone:</span>
                        <span className="text-white font-medium">{kycData?.phone_number || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between py-2">
                        <span className="text-white">KYC Status:</span>
                        <span className={`font-medium ${
                          kycData?.kyc_status === 'approved' ? 'text-green-400' :
                          kycData?.kyc_status === 'rejected' ? 'text-red-400' :
                          'text-yellow-400'
                        }`}>
                          {kycData?.kyc_status === 'approved' ? '✅ Approved' :
                           kycData?.kyc_status === 'rejected' ? '❌ Rejected' :
                           kycData?.kyc_status === 'pending' ? '⏳ Pending' :
                           kycData?.kyc_status || 'Unknown'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="bg-gray-800 border-t border-gray-700 p-4 rounded-b-lg flex justify-center space-x-4">
                <button
                  onClick={() => handleOpenDocument(selectedDocument)}
                  className="px-6 py-2 bg-green-600 hover:bg-green-500 text-white rounded transition-colors flex items-center space-x-2"
                >
                  <span>🔗</span>
                  <span>Open in New Window</span>
                </button>
                <button
                  onClick={() => handleDownloadDocument(selectedDocument)}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded transition-colors flex items-center space-x-2"
                >
                  <span>⬇️</span>
                  <span>Download</span>
                </button>
                <button
                  onClick={() => setShowImageModal(false)}
                  className="px-6 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KYCDocumentViewer;
